import React, { useState, useEffect, useRef } from "react";
import axios from "../../../utils/axios";
import { useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiSearch,
  FiDownload,
  FiEye,
  FiCalendar,
  FiFilter,
  FiRefreshCw,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiAlertCircle,
  FiMessageSquare,
  FiSmartphone,
} from "react-icons/fi";

// ── Simple Donut Chart (pure CSS/SVG — no library needed) ─────────────────────
function DonutChart({ data }) {
  const size = 120;
  const r = 45;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;

  const total = data.reduce((s, d) => s + d.value, 0);
  if (!total) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="18"
        />
      </svg>
    );
  }

  let offset = 0;
  const segments = data.map((d) => {
    const pct = d.value / total;
    const dash = pct * circumference;
    const gap = circumference - dash;
    const seg = { ...d, dash, gap, offset };
    offset += dash;
    return seg;
  });

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ transform: "rotate(-90deg)" }}
    >
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="#e5e7eb"
        strokeWidth="18"
      />
      {segments.map((seg, i) => (
        <circle
          key={i}
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={seg.color}
          strokeWidth="18"
          strokeDasharray={`${seg.dash} ${seg.gap}`}
          strokeDashoffset={-seg.offset}
        />
      ))}
    </svg>
  );
}

// ── Stats Card (top 3 cards) ─────────────────────────────────────────────────
function StatsCard({ icon, title, campaigns, stats, donutData, totalCost }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex-1 min-w-0">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{icon}</span>
          <div>
            <p className="text-base font-bold text-gray-800">{title}</p>
            <p className="text-xs text-gray-500">{campaigns} Campaigns</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400 uppercase tracking-wide">COST</p>
          <p className="text-lg font-bold text-green-600">{totalCost}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="relative flex-shrink-0">
          <DonutChart data={donutData} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-bold text-gray-600">
              {stats.submitted}
            </span>
          </div>
        </div>
        <div className="space-y-1 text-xs flex-1">
          <div className="flex justify-between">
            <span className="text-gray-500">Submitted:</span>
            <span className="font-semibold text-gray-700">
              {stats.submitted}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="flex items-center gap-1 text-gray-500">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
              Delivered:
            </span>
            <span className="font-semibold text-gray-700">
              {stats.delivered}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="flex items-center gap-1 text-gray-500">
              <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
              Failed:
            </span>
            <span className="font-semibold text-gray-700">{stats.failed}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Read:</span>
            <span className="font-semibold text-gray-700">{stats.read}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Sent:</span>
            <span className="font-semibold text-gray-700">{stats.sent}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Optout:</span>
            <span className="font-semibold text-gray-700">{stats.optout}</span>
          </div>
          <div className="flex justify-between font-semibold border-t border-gray-100 pt-1 mt-1">
            <span className="text-gray-600">Total Cost:</span>
            <span className="text-gray-800">{totalCost}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    completed: { label: "Completed", cls: "bg-green-100 text-green-700" },
    processing: { label: "Processing", cls: "bg-blue-100 text-blue-700" },
    scheduled: { label: "Scheduled", cls: "bg-yellow-100 text-yellow-700" },
    failed: { label: "Failed", cls: "bg-red-100 text-red-700" },
    cancelled: { label: "Cancelled", cls: "bg-gray-100 text-gray-600" },
    draft: { label: "Draft", cls: "bg-purple-100 text-purple-700" },
  };
  const s = map[status?.toLowerCase()] || {
    label: status,
    cls: "bg-gray-100 text-gray-600",
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${s.cls}`}>
      {s.label}
    </span>
  );
}

// ── Download CSV helper ───────────────────────────────────────────────────────
function downloadCSV(campaign) {
  const headers = ["Number", "Status", "Sent At", "Error"];
  const rows = (campaign.recipients || campaign.numbers || []).map((r) => [
    r.phone || r.number,
    r.status,
    r.sentAt || "",
    r.failReason || r.error || "",
  ]);
  const csv = [headers, ...rows]
    .map((r) => r.map((c) => `"${c}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${campaign.campaignName || "campaign"}_report.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Date Filter Bar ───────────────────────────────────────────────────────────
function DateFilterBar({
  channel,
  setChannel,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  onSearch,
}) {
  const today = new Date().toISOString().split("T")[0];
  return (
    <div className="flex items-end gap-3 flex-wrap mt-4">
      <div className="flex-1 min-w-36">
        <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
          Select channel
        </label>
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-400 bg-white"
        >
          <option value="">All</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="sms">SMS</option>
          <option value="rcs">RCS</option>
        </select>
      </div>
      <div className="flex-1 min-w-36">
        <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
          From:
        </label>
        <div className="relative">
          <FiCalendar
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={14}
          />
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-400"
          />
        </div>
      </div>
      <div className="flex-1 min-w-36">
        <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
          To:
        </label>
        <div className="relative">
          <FiCalendar
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={14}
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-400"
          />
        </div>
      </div>
      <button
        onClick={onSearch}
        className="flex items-center justify-center w-11 h-11 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-colors flex-shrink-0"
      >
        <FiSearch size={18} />
      </button>
    </div>
  );
}

// ── TAB: Campaign Report ──────────────────────────────────────────────────────
function TabCampaignReport({ campaigns, loading, onRefresh }) {
  const [channel, setChannel] = useState("");
  const [fromDate, setFromDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [filtered, setFiltered] = useState([]);

  useEffect(() => {
    setFiltered(campaigns);
  }, [campaigns]);

  const handleSearch = () => {
    let data = [...campaigns];
    if (channel) data = data.filter((c) => c.channel === channel);
    if (fromDate)
      data = data.filter((c) => new Date(c.createdAt) >= new Date(fromDate));
    if (toDate)
      data = data.filter(
        (c) => new Date(c.createdAt) <= new Date(toDate + "T23:59:59"),
      );
    setFiltered(data);
  };

  return (
    <div>
      <DateFilterBar
        channel={channel}
        setChannel={setChannel}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        onSearch={handleSearch}
      />
      <div className="mt-6 space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <FiMessageSquare size={36} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No campaigns found</p>
          </div>
        ) : (
          filtered.map((c) => <CampaignCard key={c._id} campaign={c} />)
        )}
      </div>
    </div>
  );
}

function CampaignCard({ campaign: c }) {
  const [expanded, setExpanded] = useState(false);
  const total = c.totalCount || c.totalRecipients || 0;
  const delivered = c.deliveredCount || 0;
  const failed = c.failedCount || 0;
  const sent = c.sentCount || 0;
  const read = c.readCount || 0;

  return (
    <div className="border-2 border-gray-100 rounded-2xl overflow-hidden hover:border-green-200 transition-colors">
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xl">📱</span>
            <div>
              <span className="text-xs text-gray-500">Campaign Name: </span>
              <span className="text-sm font-bold text-gray-800">
                {c.campaignName}
              </span>
            </div>
          </div>
          <StatusBadge status={c.status} />
        </div>

        <p className="text-xs text-gray-400 mb-3 text-right">
          Campaign Date:{" "}
          {new Date(c.createdAt).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>

        <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-center mb-3">
          {[
            { label: "Total", value: total, color: "text-gray-800" },
            { label: "Delivered", value: delivered, color: "text-green-600" },
            { label: "Failed", value: failed, color: "text-red-500" },
            { label: "Sent", value: sent, color: "text-blue-500" },
            { label: "Read", value: read, color: "text-purple-500" },
            { label: "Optout", value: 0, color: "text-orange-500" },
            { label: "Other", value: 0, color: "text-gray-400" },
          ].map((s) => (
            <div key={s.label} className="bg-gray-50 rounded-xl p-2">
              <p className={`text-base font-bold ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-gray-400">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-3">
          <span>
            Campaign ID:{" "}
            <span className="text-green-600 font-medium">
              {c._id?.slice(-6) || "—"}/
            </span>
          </span>
          <span>
            BrandNumber:{" "}
            <span className="text-green-600 font-medium">
              {c.brandNumber || "—"}/
            </span>
          </span>
          <span>
            Template:{" "}
            <span className="text-green-600 font-medium">
              {c.templateName || "—"}/
            </span>
          </span>
          <span>
            /Message Type:{" "}
            <span className="text-orange-500 font-medium">
              {c.channel || "whatsapp"}
            </span>
          </span>
          <span>
            /Country Code:{" "}
            <span className="font-medium text-gray-700">
              {c.countryCode || "91"}
            </span>
          </span>
          <span>
            /Campaign Cost:{" "}
            <span className="font-medium text-gray-700">
              {c.creditsUsed || 0}
            </span>
          </span>
        </div>

        <div className="flex gap-2 justify-end">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <FiEye size={13} /> {expanded ? "Hide" : "Report"}
          </button>
          <button
            onClick={() => downloadCSV(c)}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-green-200 rounded-lg text-xs text-green-600 hover:bg-green-50 transition-colors"
          >
            <FiDownload size={13} /> Download
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 p-4">
          <p className="text-xs font-semibold text-gray-600 mb-3">
            Number-wise Report
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-500 border-b border-gray-200">
                  <th className="text-left py-2 pr-4">#</th>
                  <th className="text-left py-2 pr-4">Number</th>
                  <th className="text-left py-2 pr-4">Status</th>
                  <th className="text-left py-2 pr-4">Sent At</th>
                  <th className="text-left py-2">Error</th>
                </tr>
              </thead>
              <tbody>
                {(c.recipients || c.numbers || []).slice(0, 50).map((r, i) => (
                  <tr
                    key={i}
                    className="border-b border-gray-100 hover:bg-white"
                  >
                    <td className="py-1.5 pr-4 text-gray-400">{i + 1}</td>
                    <td className="py-1.5 pr-4 font-medium text-gray-700">
                      {r.phone || r.number}
                    </td>
                    <td className="py-1.5 pr-4">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          r.status === "sent" || r.status === "delivered"
                            ? "bg-green-100 text-green-700"
                            : r.status === "failed"
                              ? "bg-red-100 text-red-600"
                              : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="py-1.5 pr-4 text-gray-400">
                      {r.sentAt
                        ? new Date(r.sentAt).toLocaleString("en-IN")
                        : "—"}
                    </td>
                    <td className="py-1.5 text-red-400 text-[10px]">
                      {r.failReason || r.error || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(c.recipients || c.numbers || []).length > 50 && (
              <p className="text-xs text-gray-400 mt-2 text-center">
                Showing first 50 — download CSV for full report
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── TAB: Number Filter Search ─────────────────────────────────────────────────
function TabNumberFilter({ campaigns }) {
  const [phone, setPhone] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);

  const handleSearch = () => {
    if (!phone.trim()) return;
    const found = [];
    campaigns.forEach((c) => {
      const list = c.recipients || c.numbers || [];
      list.forEach((r) => {
        if ((r.phone || r.number || "").includes(phone.trim())) {
          found.push({
            campaign: c.campaignName,
            number: r.phone || r.number,
            status: r.status,
            date: c.createdAt,
            sentAt: r.sentAt,
          });
        }
      });
    });
    setResults(found);
    setSearched(true);
  };

  return (
    <div>
      <div className="mt-4 flex gap-3">
        <div className="relative flex-1">
          <FiSmartphone
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={14}
          />
          <input
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Enter mobile number to search..."
            className="w-full pl-9 pr-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-400"
          />
        </div>
        <button
          onClick={handleSearch}
          className="flex items-center justify-center w-12 h-12 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-colors"
        >
          <FiSearch size={18} />
        </button>
      </div>

      {searched && (
        <div className="mt-4">
          {results.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-8">
              No results found for "{phone}"
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-xs text-gray-500 uppercase tracking-wide">
                    <th className="text-left px-4 py-3">Campaign</th>
                    <th className="text-left px-4 py-3">Number</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Campaign Date</th>
                    <th className="text-left px-4 py-3">Sent At</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr
                      key={i}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {r.campaign}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{r.number}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {new Date(r.date).toLocaleDateString("en-IN")}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {r.sentAt
                          ? new Date(r.sentAt).toLocaleString("en-IN")
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── TAB: Template Summary ─────────────────────────────────────────────────────
function TabTemplateSummary({ campaigns }) {
  const [channel, setChannel] = useState("");
  const [fromDate, setFromDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [summary, setSummary] = useState([]);
  const [searched, setSearched] = useState(false);

  const handleSearch = () => {
    let data = [...campaigns];
    if (channel) data = data.filter((c) => c.channel === channel);
    if (fromDate)
      data = data.filter((c) => new Date(c.createdAt) >= new Date(fromDate));
    if (toDate)
      data = data.filter(
        (c) => new Date(c.createdAt) <= new Date(toDate + "T23:59:59"),
      );

    const grouped = {};
    data.forEach((c) => {
      const key = c.templateName || c.templateId || "Unknown";
      if (!grouped[key])
        grouped[key] = {
          template: key,
          campaigns: 0,
          total: 0,
          sent: 0,
          delivered: 0,
          failed: 0,
          read: 0,
        };
      grouped[key].campaigns++;
      grouped[key].total += c.totalCount || 0;
      grouped[key].sent += c.sentCount || 0;
      grouped[key].delivered += c.deliveredCount || 0;
      grouped[key].failed += c.failedCount || 0;
      grouped[key].read += c.readCount || 0;
    });
    setSummary(Object.values(grouped));
    setSearched(true);
  };

  return (
    <div>
      <DateFilterBar
        channel={channel}
        setChannel={setChannel}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        onSearch={handleSearch}
      />
      <div className="mt-6">
        {!searched ? (
          <p className="text-center text-sm text-gray-400 py-8">
            Select filters and click search
          </p>
        ) : summary.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-8">
            No Record Found
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-xs text-gray-500 uppercase tracking-wide">
                  {[
                    "Template",
                    "Campaigns",
                    "Total",
                    "Sent",
                    "Delivered",
                    "Failed",
                    "Read",
                  ].map((h) => (
                    <th key={h} className="text-left px-4 py-3">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summary.map((s, i) => (
                  <tr
                    key={i}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 font-medium text-green-600">
                      {s.template}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{s.campaigns}</td>
                    <td className="px-4 py-3 text-gray-700">{s.total}</td>
                    <td className="px-4 py-3 text-blue-600 font-medium">
                      {s.sent}
                    </td>
                    <td className="px-4 py-3 text-green-600 font-medium">
                      {s.delivered}
                    </td>
                    <td className="px-4 py-3 text-red-500 font-medium">
                      {s.failed}
                    </td>
                    <td className="px-4 py-3 text-purple-600 font-medium">
                      {s.read}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── TAB: Day Wise Summary ─────────────────────────────────────────────────────
function TabDayWiseSummary({ campaigns }) {
  const [channel, setChannel] = useState("");
  const [fromDate, setFromDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [summary, setSummary] = useState([]);
  const [searched, setSearched] = useState(false);

  const handleSearch = () => {
    let data = [...campaigns];
    if (channel) data = data.filter((c) => c.channel === channel);
    if (fromDate)
      data = data.filter((c) => new Date(c.createdAt) >= new Date(fromDate));
    if (toDate)
      data = data.filter(
        (c) => new Date(c.createdAt) <= new Date(toDate + "T23:59:59"),
      );

    const grouped = {};
    data.forEach((c) => {
      const day = new Date(c.createdAt).toLocaleDateString("en-IN");
      if (!grouped[day])
        grouped[day] = {
          day,
          campaigns: 0,
          total: 0,
          sent: 0,
          delivered: 0,
          failed: 0,
          read: 0,
        };
      grouped[day].campaigns++;
      grouped[day].total += c.totalCount || 0;
      grouped[day].sent += c.sentCount || 0;
      grouped[day].delivered += c.deliveredCount || 0;
      grouped[day].failed += c.failedCount || 0;
      grouped[day].read += c.readCount || 0;
    });
    setSummary(
      Object.values(grouped).sort((a, b) => new Date(b.day) - new Date(a.day)),
    );
    setSearched(true);
  };

  return (
    <div>
      <DateFilterBar
        channel={channel}
        setChannel={setChannel}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        onSearch={handleSearch}
      />
      <div className="mt-6">
        {!searched ? (
          <p className="text-center text-sm text-gray-400 py-8">
            Select filters and click search
          </p>
        ) : summary.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-8">
            No Record Found
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-xs text-gray-500 uppercase tracking-wide">
                  {[
                    "Date",
                    "Campaigns",
                    "Total",
                    "Sent",
                    "Delivered",
                    "Failed",
                    "Read",
                  ].map((h) => (
                    <th key={h} className="text-left px-4 py-3">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summary.map((s, i) => (
                  <tr
                    key={i}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {s.day}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{s.campaigns}</td>
                    <td className="px-4 py-3 text-gray-700">{s.total}</td>
                    <td className="px-4 py-3 text-blue-600 font-medium">
                      {s.sent}
                    </td>
                    <td className="px-4 py-3 text-green-600 font-medium">
                      {s.delivered}
                    </td>
                    <td className="px-4 py-3 text-red-500 font-medium">
                      {s.failed}
                    </td>
                    <td className="px-4 py-3 text-purple-600 font-medium">
                      {s.read}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── TAB: Schedule Campaign ────────────────────────────────────────────────────
function TabScheduleCampaign({ campaigns }) {
  const navigate = useNavigate();
  const scheduled = campaigns.filter(
    (c) => c.status === "scheduled" || c.isScheduled,
  );

  return (
    <div className="mt-4">
      {scheduled.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <FiClock size={36} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No scheduled campaigns</p>
          <button
            onClick={() => navigate("/campaign/create")}
            className="mt-4 text-green-600 text-sm font-semibold hover:underline"
          >
            + Create Scheduled Campaign
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {scheduled.map((c) => (
            <div
              key={c._id}
              className="border-2 border-yellow-100 rounded-2xl p-4 bg-yellow-50/30"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-800">
                  {c.campaignName}
                </span>
                <StatusBadge status={c.status} />
              </div>
              <div className="text-xs text-gray-500 space-y-1">
                <p>
                  Template:{" "}
                  <span className="font-medium text-gray-700">
                    {c.templateName}
                  </span>
                </p>
                <p>
                  Audience:{" "}
                  <span className="font-medium text-gray-700">
                    {c.totalCount} numbers
                  </span>
                </p>
                {c.scheduleStart && (
                  <p>
                    Starts:{" "}
                    <span className="font-medium text-gray-700">
                      {new Date(c.scheduleStart).toLocaleString("en-IN")}
                    </span>
                  </p>
                )}
                {c.scheduleEnd && (
                  <p>
                    Ends:{" "}
                    <span className="font-medium text-gray-700">
                      {new Date(c.scheduleEnd).toLocaleString("en-IN")}
                    </span>
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function CampaignReport() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("report");

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/campaigns?limit=100");
      if (res.data.success) setCampaigns(res.data.data);
    } catch (err) {
      console.error("Failed to load campaigns:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Compute top-level stats
  const waStats = {
    submitted: campaigns
      .filter((c) => c.channel === "whatsapp")
      .reduce((s, c) => s + (c.totalCount || 0), 0),
    delivered: campaigns
      .filter((c) => c.channel === "whatsapp")
      .reduce((s, c) => s + (c.deliveredCount || 0), 0),
    failed: campaigns
      .filter((c) => c.channel === "whatsapp")
      .reduce((s, c) => s + (c.failedCount || 0), 0),
    sent: campaigns
      .filter((c) => c.channel === "whatsapp")
      .reduce((s, c) => s + (c.sentCount || 0), 0),
    read: campaigns
      .filter((c) => c.channel === "whatsapp")
      .reduce((s, c) => s + (c.readCount || 0), 0),
    optout: 0,
  };
  const waCampaigns = campaigns.filter((c) => c.channel === "whatsapp").length;
  const waCost = campaigns
    .filter((c) => c.channel === "whatsapp")
    .reduce((s, c) => s + (c.creditsUsed || 0), 0);

  const waDonut = [
    { value: waStats.delivered, color: "#22c55e" },
    { value: waStats.failed, color: "#ef4444" },
    { value: waStats.read, color: "#f97316" },
    {
      value: Math.max(
        0,
        waStats.submitted - waStats.delivered - waStats.failed - waStats.read,
      ),
      color: "#d1d5db",
    },
  ];

  const TABS = [
    { id: "report", label: "Campaign Report" },
    { id: "number", label: "Number Filter Search" },
    { id: "template", label: "Template Summary" },
    { id: "daywise", label: "Day Wise Summary" },
    { id: "schedule", label: "Schedule Campaign" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <h1 className="text-xl font-bold text-gray-800">Campaign List</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchCampaigns}
            className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <FiRefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={() => navigate("/campaign/create")}
            className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-sm shadow-green-200 transition-colors"
          >
            <FiPlus size={16} /> Create New Campaign
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="flex gap-4 flex-wrap">
          <StatsCard
            icon="💬"
            title="Total WhatsApp"
            campaigns={waCampaigns}
            stats={waStats}
            donutData={waDonut}
            totalCost={waCost}
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          {/* Tab Bar */}
          <div className="flex gap-1 p-1 border-b border-gray-100 overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
                  activeTab === t.id
                    ? "bg-gray-800 text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-5">
            {activeTab === "report" && (
              <TabCampaignReport
                campaigns={campaigns}
                loading={loading}
                onRefresh={fetchCampaigns}
              />
            )}
            {activeTab === "number" && (
              <TabNumberFilter campaigns={campaigns} />
            )}
            {activeTab === "template" && (
              <TabTemplateSummary campaigns={campaigns} />
            )}
            {activeTab === "daywise" && (
              <TabDayWiseSummary campaigns={campaigns} />
            )}
            {activeTab === "schedule" && (
              <TabScheduleCampaign campaigns={campaigns} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
